/**
 * Turning a point on the airframe — a `.TBL` model vertex, or a `.DAT` gun
 * position, which share a frame — into the engine's body frame, relative to
 * the pose origin.
 *
 * Two conversions compose, and getting either wrong is silent:
 *
 * 1. **Origin.** A `.TBL`'s own origin is not the centre of the airframe (a
 *    Camel's sits up by the engine), and `apps/web`'s `aircraft-mesh.ts`
 *    recentres every mesh onto its bounding box before it reaches world space
 *    — so *the pose origin is the drawn mesh's bounding-box centre*, not the
 *    `.TBL` origin. A muzzle expressed against the `.TBL` origin would be
 *    drawn up to 1.8 m behind the aeroplane it belongs to. `MODEL_CENTRE_UNITS`
 *    is that recentring, per aircraft, in model units.
 * 2. **Axes.** Baked models are X-right, Y-longitudinal (nose positive), Z-up;
 *    the engine and three.js share Y-up with forward −Z. The map is
 *    `(x, y, z) → (x, z, −y)` — the same one `apps/web`'s
 *    `world/placement-transform.ts` applies to scenery, and the same −90°
 *    pitch about X that `MODEL_TO_WORLD_PITCH_RAD` performs on the mesh.
 *
 * `MODEL_CENTRE_UNITS` is the one table in the flight core that is **not** a
 * read of `data/original` — it is measured off `data/baked/models/<key>.json`,
 * exactly as `loadAircraftMesh` measures it (the bounding box of the
 * *triangle* positions of the highest-index LOD that has triangles; lines,
 * billboards and sphere nodes are not in the mesh geometry and so are not in
 * its box). `tools/bake`'s test recomputes all 33 from the real models and
 * asserts them, so it cannot drift away from what the renderer does. Every
 * aircraft's X centre is 0 because every model is an exact ±X mirror pair
 * (docs/formats/models-tbl.md §4.2) — that is a property of the data, and the
 * bake test checks it rather than this file assuming it.
 */

import type { Vec3 } from "./math3d.ts";
import type { AircraftKey } from "./aircraft-table.ts";

/** `.TBL` vertex units per metre — `docs/formats/models-tbl.md` §4.2, from `FOKD7`'s 577-unit upper wing over a real 8.90 m. */
export const MODEL_UNITS_PER_METRE = 65;

/**
 * Bounding-box centre of each aircraft's drawn mesh, in model units
 * (X right, Y nose, Z up). Subtracting it takes a point from the `.TBL`
 * origin to the pose origin.
 */
export const MODEL_CENTRE_UNITS = {
  MORANE:   [0,  -73.5, -14],
  FOKE3:    [0, -162.5,  -9],
  AIRCO:    [0, -126.5,  -9],
  NIEU11:   [0, -125,   -19.5],
  NIEUPORT: [0, -115.5, -20.5],
  SOPPUP:   [0, -121.5, -26.5],
  SPAD_7:   [0, -124,    -6],
  ALBA_D2:  [0, -115.5, -12.5],
  SOPTRI:   [0, -129.5,  -3.5],
  ALBA_D3:  [0, -115.5, -12.5],
  HALBD2:   [0, -137,   -15.5],
  SE5:      [0, -114,    -7],
  ALBA_D5:  [0, -115.5, -12.5],
  CAMEL:    [0, -120.5, -26.5],
  PFALZ:    [0, -131,   -12.5],
  FOKDR1:   [0, -131,   -10.5],
  SPAD_13:  [0, -124,    -6],
  NIEU28:   [0, -111,   -24.5],
  FOKD7:    [0, -137.5, -15.5],
  SIEMENS:  [0,  -96,   -25],
  SNIPE:    [0, -123.5, -29.5],
  FOKD8:    [0, -107,   -23],
  BE2:      [0, -172,     1],
  AVIATIK:  [0, -178,   -24.5],
  FE2:      [0, -240,    19.5],
  ROLAND:   [0, -140.5, -12],
  RE8:      [0, -171,     1],
  RUMPLER:  [0, -178,   -20.5],
  BRISTOL:  [0, -192,   -21.5],
  DH4:      [0, -196,     1.5],
  JUNKER:   [0, -121.5,  -5],
  GOTHA:    [0, -240,    18],
  HANDLEY:  [0, -347.5,   0],
} satisfies Record<AircraftKey, readonly [x: number, y: number, z: number]>;

/**
 * A point given in model units against the `.TBL` origin, as body-frame
 * metres against the pose origin: recentred, scaled, and pitched from Z-up
 * into Y-up.
 */
export function modelPointToBodyMetres(key: AircraftKey, rightUnits: number, forwardUnits: number, upUnits: number): Vec3 {
  const centre = MODEL_CENTRE_UNITS[key];
  return {
    x: (rightUnits - centre[0]) / MODEL_UNITS_PER_METRE,
    y: (upUnits - centre[2]) / MODEL_UNITS_PER_METRE,
    z: -(forwardUnits - centre[1]) / MODEL_UNITS_PER_METRE,
  };
}

/** One point of a `GroundHull`: how far **behind** the pose origin it lies, and how far **above** it, in model units. */
export type HullPointUnits = readonly [back: number, up: number];

/**
 * The part of an aircraft's drawn shape that can ever touch the ground: the
 * lower convex hull of its mesh in the body-frame (back, up) plane, in model
 * units against the pose origin.
 *
 * **What it is for.** An aeroplane standing on a surface stands at the height
 * where its own lowest drawn point reaches that surface, and which point that
 * is depends on how it is pitched — a taildragger on its wheels and the same
 * taildragger with its tail up touch the ground with different parts of
 * themselves, at different heights. `stanceHeightM` is that height, and it is
 * exactly the support function of this point set, so the hull is all of the
 * mesh that the answer can depend on: every other vertex is inside it, and a
 * point inside a convex hull is never the extreme one in any direction.
 * Measured over −20°…+30° of pitch the hull's answer and the whole mesh's agree
 * to **0 m** on all 33 aircraft, which is what makes this a compression of the
 * mesh rather than an approximation of it.
 *
 * **Frame.** Same as `MODEL_CENTRE_UNITS`, and recentred on the same box: the
 * model frame's `y` (nose positive) negated into "back", its `z` kept as "up",
 * both in model units against the pose origin.
 *
 * **Both drawings of the aeroplane, deliberately.** This port draws every
 * airframe twice — the authentic `.TBL` and, with ENHANCED MODELS on (which is
 * the default), an offline-built `.glb` (`docs/engineering/enhanced-models.md`)
 * — and the sim has to give **one** answer for both, because that switch is a
 * client-side rendering setting and a lockstep peer, a replay and a census must
 * not be able to disagree about where an aeroplane's wheels stop. So the hull
 * is the lower hull of the two together: the height at which *neither* drawing
 * is underground. The two differ almost entirely in the **tail skid** — the
 * authentic meshes mostly have none, their rear fuselage tapering up where the
 * enhanced model carries a skid — so with ENHANCED MODELS off an aeroplane
 * stands up to 0.25 m clear of the ground, standing on a skid that LOD does not
 * draw. §8.3.1 of `docs/engineering/flight-tunables.md` has the per-type table
 * and why this way round: the alternative put the *default* picture up to
 * 0.50 m underground.
 *
 * Sources, per side: the triangle positions of the highest-index LOD with
 * triangles in `data/baked/models/<key>.json` (lines, billboards and sphere
 * nodes excluded for the same reason they are excluded from the centre — they
 * are not in the geometry `aircraft-mesh.ts` builds), and every mesh under
 * `LOD0` in `data/enhanced/models/<key>.glb` except the propeller, whose
 * lowest point depends on where the blade happens to be stopped. `tools/bake`'s
 * test recomputes the authentic half and `tools/enhanced`'s the whole union, so
 * this cannot drift away from either mesh. The propeller is excluded and
 * nothing changes: on all 33 it is inside the hull at every attitude an
 * aeroplane on its wheels can reach.
 */
export const MODEL_GROUND_HULL_UNITS = {
  MORANE:    [[-158.5, 12.5], [-77.5, -56.5], [-72.5, -60], [-67, -61], [-61, -60], [177, -10], [178, -8], [178.5, -6.5]],
  FOKE3:     [[-237.5, -26], [-234.5, -33], [-233.5, -34.5], [-158, -77], [-151, -78], [-144.5, -77], [210, -12], [211, -10], [211.5, -8.5], [225.5, 37], [225.5, 54]],
  AIRCO:     [[-207.5, 2], [-194.5, -23], [-121.5, -84], [-116.5, -87], [-110.5, -88.5], [190, -45], [191.5, -41.5], [202.5, -7], [207.5, 24]],
  NIEU11:    [[-195, 5.5], [-189.5, -60.5], [-187.5, -69], [-187, -70.5], [-183, -76.5], [-175.5, -81], [-167, -83], [169.5, -4.5], [195, 39.5], [195, 74.5]],
  NIEUPORT:  [[-185.5, -0.5], [-157.5, -67.5], [-155, -71], [-150, -74.5], [-144, -75.5], [-138.5, -74.5], [150, -14], [178.5, 20.5], [185.5, 41.5]],
  SOPPUP:    [[-188.5, -8.5], [-168, -82.5], [-167.5, -83.5], [-164, -88.5], [-158, -92.5], [-150.5, -94], [-143, -92.5], [158, -20.5], [178.5, 17.5], [188.5, 44.5], [188.5, 54.5]],
  SPAD_7:    [[-204, -15], [-166, -70], [-161.5, -76.5], [-155, -81], [-146.5, -83], [172, -30.5], [204, 14], [204, 78]],
  ALBA_D2:   [[-232.5, 11.5], [-137.5, -76.5], [-132.5, -80], [-126, -81.5], [182, -18.5], [232.5, 28.5]],
  SOPTRI:    [[-199.5, -10.5], [-159, -93.5], [-158.5, -94.5], [-155.5, -98.5], [-150.5, -101.5], [-145, -103], [135, -36.5], [185.5, 3.5], [199.5, 31.5]],
  ALBA_D3:   [[-232.5, 11.5], [-137.5, -76.5], [-132.5, -80], [-126, -81.5], [182, -18.5], [232.5, 28.5]],
  HALBD2:    [[-223, 13.5], [-185, -69.5], [-181, -75.5], [-173.5, -80], [-165, -82], [237.5, -10.5], [238.5, -8.5], [239, -7]],
  SE5:       [[-183, -20], [-174, -28.5], [-124.5, -69], [-119.5, -72], [-114, -73], [186.5, -35], [188, -31.5]],
  ALBA_D5:   [[-232.5, 11.5], [-137.5, -76.5], [-132.5, -80], [-126, -81.5], [182, -18.5], [232.5, 28.5]],
  CAMEL:     [[-187.5, -5.5], [-164.5, -81.5], [-161, -86.5], [-155.5, -90], [-149, -91.5], [-142.5, -90], [158, -15.5], [177.5, 16.5], [187.5, 52.5]],
  PFALZ:     [[-262, 12.5], [-156, -87], [-150, -91], [-143, -92.5], [-135.5, -91], [217.5, -18.5], [256, 30.5], [262, 68.5]],
  FOKDR1:    [[-173, -11.5], [-138, -77], [-135, -81.5], [-130.5, -85], [-125, -86], [-119.5, -85], [97.5, -44.5], [160, 10.5], [173, 33.5]],
  SPAD_13:   [[-204, -15], [-166, -70], [-161.5, -76.5], [-155, -81], [-146.5, -83], [172, -30.5], [204, 14], [204, 78]],
  NIEU28:    [[-191, 4.5], [-161, -70.5], [-157, -76.5], [-149.5, -81], [-141, -83], [-132.5, -81], [153.5, -7.5], [185, 28.5], [191, 40.5], [191, 64.5]],
  FOKD7:     [[-222.5, -7.5], [-151.5, -78.5], [-145, -82.5], [-137.5, -84.5], [179, -29.5], [216.5, 8.5], [222.5, 22.5], [222.5, 56.5]],
  SIEMENS:   [[-184, -9], [-167, -64.5], [-166, -67], [-162.5, -71.5], [-155.5, -76], [-147.5, -77.5], [-139.5, -76], [127.5, -19], [184, 15]],
  SNIPE:     [[-191.5, -2.5], [-170.5, -81], [-169.5, -82.5], [-166, -87], [-160, -91.5], [-152.5, -92.5], [-145, -91.5], [160, -12.5], [182.5, 28.5], [191.5, 54.5], [191.5, 64.5]],
  FOKD8:     [[-191, 13], [-156.5, -62], [-155, -65], [-152, -69], [-145, -73.5], [-137, -75], [-129, -73.5], [131.5, -13.5], [161, 1], [191, 41], [191, 59]],
  BE2:       [[-272, -21], [-185.5, -88.5], [-179, -92.5], [-172, -94], [211, -54.5], [242.5, -47.5], [252, -41], [268, -9], [272, 23]],
  AVIATIK:   [[-283, 8.5], [-204, -86.5], [-202, -88.5], [-197.5, -91], [-190, -92.5], [-182.5, -91], [191.5, -15], [282.5, 32], [283, 32.5]],
  FE2:       [[-393.5, 39], [-332, -130], [-328.5, -135], [-323.5, -138.5], [-318, -139.5], [-178, -152.5], [-167.5, -150.5], [245, -54], [324, -13.5], [333, 40.5]],
  ROLAND:    [[-269.5, 12], [-165, -86.5], [-159, -90.5], [-151.5, -92], [-144, -90.5], [208, -18.5], [269.5, 30]],
  RE8:       [[-273, -15], [-185.5, -91.5], [-179, -96], [-171, -97.5], [245.5, -49.5], [269, -11], [273, 23]],
  RUMPLER:   [[-292.5, 21.5], [-204, -90.5], [-202, -92.5], [-197.5, -95], [-190, -96.5], [-182.5, -95], [191.5, -19], [291, 28.5]],
  BRISTOL:   [[-306, -5.5], [-181.5, -101.5], [-174, -106.5], [-165, -108.5], [267.5, -30], [279, -20.5], [288, -8.5], [303, 12.5], [306, 48.5]],
  DH4:       [[-315, -36.5], [-213, -105], [-205, -110], [-196, -112], [278.5, -41.5], [315, -1.5], [315, 54.5]],
  JUNKER:    [[-236.5, 0], [-143.5, -84], [-138.5, -87.5], [-132, -89], [203, -17.5], [236.5, 31], [237.5, 62.5]],
  GOTHA:     [[-570.5, 55.5], [-546, -33], [-287.5, -166], [-277, -168], [-276, -168], [450, -69], [531, -12], [546, 32], [547, 35]],
  // The last point moved 224.5 -> 209 when the mainplanes were rigged
  // (`cockpit-3d-design.md` §19). It is the chain's terminus at the tail-most
  // station rather than anything the aeroplane stands on — the low points
  // (-33.5 here) are unchanged — and what moved is a tail bracing end that
  // `anchor.landed` re-walked onto a wing surface that had turned. The Handley
  // Page is the only airframe in the fleet whose table moved at all.
  HANDLEY:   [[-839.5, 108.5], [-804.5, -47], [-417, -219], [-402, -222], [-401.5, -222], [680.5, -76], [804.5, -33.5], [804.5, 209]],
} satisfies Record<AircraftKey, readonly HullPointUnits[]>;

/**
 * How high the pose origin sits when the aeroplane, pitched `pitchRad` nose-up,
 * is resting on a level surface — i.e. the height at which its own lowest drawn
 * point just touches.
 *
 * The support function of `MODEL_GROUND_HULL_UNITS[key]` in the downward
 * direction, which is the whole of it: rotating the body nose-up by θ takes a
 * body point `(back, up)` to a world height `up·cosθ − back·sinθ`, and the
 * origin has to stand as far above the surface as the smallest of those is
 * below it.
 */
export function stanceHeightM(hull: readonly HullPointUnits[], pitchRad: number): number {
  const cos = Math.cos(pitchRad);
  const sin = Math.sin(pitchRad);
  let deepest = -Infinity;
  for (const [back, up] of hull) {
    const below = back * sin - up * cos;
    if (below > deepest) deepest = below;
  }
  return deepest / MODEL_UNITS_PER_METRE;
}

/**
 * The nose-up pitch this airframe *rests* at — the manual's three-point
 * attitude, read off the drawn shape rather than chosen.
 *
 * `stanceHeightM` is convex in the pitch (a maximum of sinusoids), and its
 * minimum is the one attitude at which two hull points touch the surface at
 * once: the wheels and the tail skid together, which is exactly what
 * "the wheels and tail skid all meet the ground simultaneously" describes. It
 * is also a *limit* rather than a preference — past it the aeroplane would have
 * to lift its own wheels off the ground to pitch further nose-up — which is why
 * `buildUndercarriage` takes it as a cap on the ground attitude and not as the
 * attitude itself.
 *
 * Found exactly rather than by search: the minimum sits on a hull *edge* lying
 * flat, and an edge `(Δback, Δup)` lies flat at `atan2(Δup, Δback)`. There are
 * at most eight of them, so all are tried and the lowest wins.
 */
export function restingPitchRad(hull: readonly HullPointUnits[]): number {
  let bestPitch = 0;
  let bestHeight = Infinity;
  for (let i = 1; i < hull.length; i += 1) {
    const [backA, upA] = hull[i - 1]!;
    const [backB, upB] = hull[i]!;
    const pitch = Math.atan2(upB - upA, backB - backA);
    const height = stanceHeightM(hull, pitch);
    if (height < bestHeight) {
      bestHeight = height;
      bestPitch = pitch;
    }
  }
  return bestPitch;
}
